import { CalendarIcon, ClockIcon, ClockGreyIcon } from '@/components/SvgIcons';
import { BorderBottom } from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';
import {
  DateField,
  DateFieldProps,
  DesktopDatePicker,
  DesktopDatePickerProps,
  TimeField,
  TimeFieldProps,
  DesktopTimePicker,
  DesktopTimePickerProps,
} from '@mui/x-date-pickers';
import { forwardRef, useState } from 'react';

export function DateFieldCustom(props: DateFieldProps<any>) {
  const { InputProps, ...otherProps } = props;

  return (
    <StyledDateField
      InputProps={{
        ...InputProps,
        onPaste: (e: any) => {
          e.preventDefault();
        },
        onBlur: (e: any) => {
          e.preventDefault();
        },
        startAdornment: (
          <InputAdornment position="start">
            <CalendarIcon />
          </InputAdornment>
        ),
      }}
      {...otherProps}
    />
  );
}

export function DatePickerCustom(
  props: DesktopDatePickerProps<any> & {
    helperText?: string;
    variant?: 'filled';
  },
) {
  const { helperText, variant, ...otherProps } = props;
  const [open, setOpen] = useState<boolean>(false);

  return (
    <StyledDatePicker
      {...otherProps}
      open={open}
      onClose={() => setOpen(false)}
      slotProps={{
        field: {
          readOnly: true,
        },
        textField: {
          variant: variant ? variant : 'outlined',
          onClick: () => setOpen(true),
          InputProps: {
            startAdornment: (
              <InputAdornment position="start">
                <CalendarIcon />
              </InputAdornment>
            ),
          },
          helperText: helperText,
        },
        openPickerButton: {
          disabled: true,
          style: { display: 'none' },
        },
      }}
    />
  );
}

export function TimeFieldCustom(props: TimeFieldProps<any>) {
  const { InputProps, variant, ...otherProps } = props;

  return (
    <StyledTimeField
      InputProps={{
        ...InputProps,
        startAdornment: (
          <InputAdornment position="start">
            <ClockIcon />
          </InputAdornment>
        ),
      }}
      {...otherProps}
    />
  );
}

interface TimePickerCustomProps extends DesktopTimePickerProps<any> {
  helperText?: string;
  variant?: 'filled';
  error?: boolean;
  inputStyle?: 'default' | 'partner';
}

const TimePickerCustom = forwardRef<any, TimePickerCustomProps>((props, ref) => {
  const { helperText, variant, error, inputStyle = 'default', slots, slotProps, ...otherProps } = props;
  const [open, setOpen] = useState<boolean>(false);

  return (
    <StyledTimePicker
      inputStyle={inputStyle}
      {...otherProps}
      open={open}
      onClose={() => setOpen(false)}
      slotProps={{
        ...slotProps,
        field: {
          readOnly: true,
          ...slotProps?.field,
        },
        textField: {
          variant: variant ? variant : 'outlined',
          onClick: () => setOpen(true),
          helperText: helperText,
          error: error,
          ...slotProps?.textField,
        },
        openPickerButton: {
          disabled: true,
          ...slotProps?.openPickerButton,
        },
      }}
      slots={{
        openPickerIcon: ClockGreyIcon,
        ...slots,
      }}
    />
  );
});

TimePickerCustom.displayName = 'TimePickerCustom';

export { TimePickerCustom };

const styleDateTime = {
  width: '100%',
  height: 'auto',

  '.MuiOutlinedInput-root': {
    height: '48px',
    paddingLeft: '16px',
    fontSize: '14px',
    lineHeight: '16px',

    '&.Mui-focused': {
      '.MuiInputAdornment-root': {
        '& svg': {
          //   color: '#D5A239',
        },
      },
    },

    '.MuiInputAdornment-root': {
      margin: '0px',

      '& svg': {
        height: '20px',
        width: '20px',
      },
    },

    input: {
      height: '100%',
      padding: '0px 16px 0px 12px',

      '&::placeholder': {},
    },

    fieldset: {
      borderRadius: '8px',
    },
  },
};

const StyledDateField = styled(DateField)(({ theme }) => ({
  ...styleDateTime,
  [theme.breakpoints.down('sm')]: {
    '.MuiOutlinedInput-root': {
      fontSize: 'unset',
      lineHeight: 'unset',
    },
  },
}));

const StyledDatePicker = styled(DesktopDatePicker)(({ theme }) => ({
  ...styleDateTime,
  [theme.breakpoints.down('sm')]: {
    '.MuiOutlinedInput-root': {
      fontSize: 'unset',
      lineHeight: 'unset',
    },
  },
  label: {
    lineHeight: 'unset',
  },

  '& .MuiFilledInput-root': {
    height: '48px',
    borderRadius: 0,
    fontSize: '14px',
    backgroundColor: 'transparent',
    border: 'unset',
    borderBottom: `1px solid ${theme.palette.white[80]}`,
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    '&:before': {
      borderBottom: 'unset',
    },
    '&:hover': {
      borderBottomColor: theme.palette.primary.main,
      backgroundColor: 'transparent',
      '&:before': {
        borderBottom: 'unset',
      },
      '&:after': {
        borderBottom: 'unset',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent',
      borderBottomColor: theme.palette.primary.main,
      '&:after': {
        borderBottom: 'unset',
      },
    },
    '&.Mui-readOnly': {
      '&:hover': {
        backgroundColor: 'transparent',
        boxShadow: 'unset',
        '&::before': {
          border: 'unset',
        },
      },
      '&:before': {
        borderBottom: 'unset',
      },
    },
    '&.Mui-error': {
      borderColor: theme.palette.error.main,
    },
  },
}));

const StyledTimeField = styled(TimeField)(({ theme }) => ({
  ...styleDateTime,
  [theme.breakpoints.down('sm')]: {
    '.MuiOutlinedInput-root': {
      fontSize: 'unset',
      lineHeight: 'unset',
    },
  },
}));

const StyledTimePicker = styled(DesktopTimePicker)<{ inputStyle?: 'default' | 'partner' }>(({ theme, inputStyle }) => ({
  ...styleDateTime,
  [theme.breakpoints.down('sm')]: {
    '.MuiOutlinedInput-root': {
      fontSize: 'unset',
      lineHeight: 'unset',
    },
  },
  label: {
    lineHeight: 'unset',
  },

  '& .MuiFilledInput-root': inputStyle === 'partner' ? {
    height: '48px',
    borderRadius: 0,
    fontSize: '14px',
    backgroundColor: 'transparent',
    border: 'unset',
    borderBottom: `1px solid ${theme.palette.white[80]}`,
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    '&:before': {
      borderBottom: 'unset',
    },
    '&:hover': {
      borderBottomColor: theme.palette.primary.main,
      backgroundColor: 'transparent',
      '&:before': {
        borderBottom: 'unset',
      },
      '&:after': {
        borderBottom: 'unset',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent',
      borderBottomColor: theme.palette.primary.main,
      '&:after': {
        borderBottom: 'unset',
      },
    },
    '&.Mui-readOnly': {
      '&:hover': {
        borderBottomColor: theme.palette.primary.main,
        backgroundColor: 'transparent',
        boxShadow: 'unset',
        '&::before': {
          border: 'unset',
        },
      },
      '&:before': {
        borderBottom: 'unset',
      },
    },
    '&.Mui-error': {
      borderColor: theme.palette.error.main,
    },
  } : {
    // Default (old) style
    height: 'auto',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    fontSize: '14px',
    backgroundColor: 'transparent',
    borderBottom: '1px solid #0000006b',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    '&:before': {
      borderBottom: 'unset',
    },
    '&:hover': {
      borderBottom: `1px solid #0000006b !important`,
      backgroundColor: 'transparent',
      '&:before': {
        borderBottom: 'unset',
      },
      '&:after': {
        borderBottom: 'unset',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent',
      borderColor: theme.palette.primary.main,
      '&:after': {
        borderBottom: `1px solid ${theme.palette.primary.main}`,
      },
    },
    '&.Mui-readOnly': {
      '&:hover': {
        borderBottom: `1px solid #E0E3E7`,
        backgroundColor: 'transparent',
        boxShadow: 'unset',
        '&::before': {
          borderBottom: 'unset',
        },
      },
      '&:before': {
        borderBottom: 'unset',
      },
    },
    '&.Mui-error': {
      borderColor: theme.palette.error.main,
    },
  },
}));
